// leafFall.js — autumn leaf drop and cherry-blossom petal rain.
//
// Two foliage types shed: autumn leaves flutter down a few at a time (and
// actually leave the tree, so it gradually thins), and cherry blossoms are
// forever raining — a steady drift of single petals plus the occasional whole
// blossom (neither of which thins the tree). Driven once per frame from
// animate() via updateLeafFall(); it self-throttles on internal timers and is a
// no-op for every other case (and for pine, which has needles, not leaves).
// Animations use the global `gsap` (loaded via a <script> tag in index.html).

import * as THREE from 'three';
import { parameters } from './core.js';
import { isNeedleType, leafPalette } from './foliageShader.js';

let _leafFallClock = 0;            // timeSeconds at the previous tick
let _flowerTimer = 0;             // counts down to the next whole leaf/flower drop
let _petalTimer = 0;             // counts down to the next single cherry petal
const AUTUMN_FALL_INTERVAL = 1.2; // average seconds between autumn leaf drops
const CHERRY_FLOWER_INTERVAL = 1.6; // whole blossoms drop rarely...
const CHERRY_PETAL_INTERVAL = 0.4;  // ...while single petals drift down often

// What's shedding, if anything: 'cherry' (always-on petal rain) takes priority
// over 'autumn' (gradient palette). null means nothing sheds right now.
function fallMode() {
  if (parameters.leafType === 'none') return null; // bare tree: nothing to shed
  if (parameters.leafType === 'cherry') return 'cherry';
  // Larch is a deciduous conifer: its needles gild and drop in autumn like leaves
  // (its instanced needles are marked isLeaf so shedRandomLeaf can drop them). Every
  // other conifer is evergreen and never sheds.
  if (parameters.leafType === 'larch') return leafPalette() === 'autumn' ? 'autumn' : null;
  if (isNeedleType()) return null; // evergreen conifers: needles don't shed
  if (leafPalette() === 'autumn') return 'autumn';
  return null;
}

export function updateLeafFall(timeSeconds, tree) {
  // Frame delta, clamped so a backgrounded tab doesn't dump a burst of leaves.
  const dt = _leafFallClock ? Math.min(timeSeconds - _leafFallClock, 0.1) : 0;
  _leafFallClock = timeSeconds;

  const mode = tree && fallMode();
  if (!mode) return;

  if (mode === 'autumn') {
    // Autumn leaves actually drop off, so the tree gradually thins.
    _flowerTimer -= dt;
    if (_flowerTimer <= 0) {
      _flowerTimer = AUTUMN_FALL_INTERVAL * (0.5 + Math.random());
      shedRandomLeaf(tree, /* remove = */ true);
    }
    return;
  }

  // Cherry: mostly single petals, with the rare whole blossom. Neither removes
  // anything from the tree (the blossoms stay full), so this rains forever.
  _flowerTimer -= dt;
  if (_flowerTimer <= 0) {
    _flowerTimer = CHERRY_FLOWER_INTERVAL * (0.5 + Math.random());
    shedRandomLeaf(tree, /* remove = */ false);
  }
  _petalTimer -= dt;
  if (_petalTimer <= 0) {
    _petalTimer = CHERRY_PETAL_INTERVAL * (0.5 + Math.random());
    shedCherryPetal(tree);
  }
}

// A single sakura petal: one lobe of the five-petal blossom outline (the same
// radiusFn, sliced to one petal's angular sector) fanned from the flower centre,
// with the blossom's rose→white centre gradient baked into vertex colors. Built
// once at unit size and shared; each falling petal scales/clones to taste.
let _cherryPetalGeo = null;
let _cherryPetalMat = null;
function cherryPetalGeometry() {
  if (_cherryPetalGeo) return _cherryPetalGeo;
  const radiusFn = (a) => 0.6 + 0.4 * Math.cos(5 * a) - 0.12 * Math.cos(10 * a);
  const a0 = -Math.PI / 5, a1 = Math.PI / 5; // one petal, centred on a = 0 (+X)
  const SEG = 24;
  const positions = [];
  const colors = [];
  const rose = new THREE.Color(0xE0559A);
  const white = new THREE.Color(0xffffff);
  const tmp = new THREE.Color();
  const push = (x, y) => {
    positions.push(x, y, 0);
    const c = tmp.copy(rose).lerp(white, Math.min(Math.hypot(x, y) / 0.9, 1));
    colors.push(c.r, c.g, c.b);
  };
  for (let i = 0; i < SEG; i++) {
    const aA = a0 + (a1 - a0) * (i / SEG);
    const aB = a0 + (a1 - a0) * ((i + 1) / SEG);
    const rA = radiusFn(aA), rB = radiusFn(aB);
    push(0, 0);
    push(Math.cos(aA) * rA, Math.sin(aA) * rA);
    push(Math.cos(aB) * rB, Math.sin(aB) * rB);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  g.computeVertexNormals();
  _cherryPetalGeo = g;
  return g;
}
function cherryPetalMaterial() {
  if (!_cherryPetalMat) {
    _cherryPetalMat = new THREE.MeshStandardMaterial({
      vertexColors: true, roughness: 0.7, metalness: 0.1,
      side: THREE.DoubleSide, transparent: true, opacity: 0.95
    });
  }
  return _cherryPetalMat;
}

// Launch a single petal from a random blossom on the tree and let it tumble to
// the ground. Nothing is removed from the tree — the petal is a fresh little mesh
// spawned at a blossom's world position, then gsap drifts/tumbles/fades it.
function shedCherryPetal(tree) {
  const meshes = [];
  tree.mesh.traverse(o => { if (o.userData.isLeaf && o.isInstancedMesh && o.count > 0) meshes.push(o); });
  if (!meshes.length) return;
  const src = meshes[Math.floor(Math.random() * meshes.length)];
  const m = new THREE.Matrix4();
  src.getMatrixAt(Math.floor(Math.random() * src.count), m);
  const pos = new THREE.Vector3().setFromMatrixPosition(m.premultiply(src.matrixWorld));

  // Match the petal to the flower's actual size (R = base * 0.8 in the blossom).
  const R = 2.25 * (parameters.foliageSize / 15) * 0.8;
  const petal = new THREE.Mesh(cherryPetalGeometry(), cherryPetalMaterial().clone());
  petal.scale.setScalar(R * (0.8 + Math.random() * 0.4));
  petal.position.copy(pos);
  petal.rotation.set(Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28);
  petal.castShadow = true;
  petal.raycast = () => {}; // never intercept prune/shape picks on its way down

  let root = tree.mesh;
  while (root.parent) root = root.parent;
  root.add(petal);

  const dur = 4 + Math.random() * 2;
  gsap.to(petal.position, { y: '-=80', duration: dur, ease: 'power1.in' });
  gsap.to(petal.position, {
    x: `+=${(Math.random() - 0.5) * 40}`,
    z: `+=${(Math.random() - 0.5) * 40}`,
    duration: dur, ease: 'sine.inOut'
  });
  gsap.to(petal.rotation, {
    x: `+=${(Math.random() - 0.5) * 12}`,
    y: `+=${(Math.random() - 0.5) * 12}`,
    z: `+=${(Math.random() - 0.5) * 12}`,
    duration: dur, ease: 'none'
  });
  gsap.to(petal.material, {
    opacity: 0, duration: dur * 0.35, delay: dur * 0.65, ease: 'power1.in',
    onComplete: () => { root.remove(petal); petal.material.dispose(); }
  });
}

// Detach one random leaf/petal and let it flutter down: a falling copy that
// shares the branch's leaf geometry keeps the exact shape, gradient and
// per-instance colors. We re-center the kept instance onto the copy's origin so
// it tumbles about itself, reparent to the scene root so "down" is world-down,
// then gsap drifts it down with a sideways sway, a slow tumble, and a fade. When
// `remove` is set the source instance is collapsed (autumn leaves drop off);
// otherwise it stays on the branch and only a copy falls (whole cherry blossoms
// drift down without thinning the tree — see also shedCherryPetal).
function shedRandomLeaf(tree, remove) {
  const meshes = [];
  tree.mesh.traverse(o => { if (o.userData.isLeaf && o.isInstancedMesh && o.count > 0) meshes.push(o); });
  if (!meshes.length) return;

  // Pick a live (not-yet-shed) instance, retrying since some may be collapsed.
  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3(), quat = new THREE.Quaternion(), scl = new THREE.Vector3();
  let live = null, idx = -1;
  for (let attempt = 0; attempt < 8; attempt++) {
    const cand = meshes[Math.floor(Math.random() * meshes.length)];
    const i = Math.floor(Math.random() * cand.count);
    cand.getMatrixAt(i, m);
    m.decompose(pos, quat, scl);
    if (scl.x > 1e-3) { live = cand; idx = i; break; }
  }
  if (!live) return;

  // Falling copy: same count/geometry so the chosen instance keeps its colors and
  // gradient; every other instance is collapsed so only this one leaf is visible.
  const fall = new THREE.InstancedMesh(live.geometry, live.material.clone(), live.count);
  fall.material.onBeforeCompile = live.material.onBeforeCompile;
  fall.material.needsUpdate = true;
  fall.material.transparent = true;
  fall.castShadow = true;
  const collapsed = new THREE.Matrix4().makeScale(0, 0, 0);
  // Strip the kept instance's translation so the leaf sits on the copy's origin
  // (and thus tumbles about itself); that translation moves onto the mesh below.
  const centered = new THREE.Matrix4().compose(new THREE.Vector3(0, 0, 0), quat, scl);
  for (let i = 0; i < live.count; i++) fall.setMatrixAt(i, i === idx ? centered : collapsed);
  fall.instanceMatrix.needsUpdate = true;

  // Autumn: remove the leaf from the living branch. Cherry: leave it — only the
  // falling copy departs, so the tree never goes bare.
  if (remove) {
    live.setMatrixAt(idx, collapsed);
    live.instanceMatrix.needsUpdate = true;
  }

  // Place the copy at the leaf's spot in the branch frame, then reparent to the
  // scene root (preserving world transform) so it falls straight down.
  fall.position.copy(pos);
  live.parent.add(fall);
  let root = fall;
  while (root.parent) root = root.parent;
  root.attach(fall);

  // Flutter to the ground: a long drift down, a gentle side-to-side sway, a slow
  // tumble, and a fade over the back half of the fall.
  const dur = 3.5 + Math.random() * 1.5;
  gsap.to(fall.position, { y: '-=70', duration: dur, ease: 'power1.in' });
  gsap.to(fall.position, {
    x: `+=${(Math.random() - 0.5) * 30}`,
    z: `+=${(Math.random() - 0.5) * 30}`,
    duration: dur, ease: 'sine.inOut'
  });
  gsap.to(fall.rotation, {
    x: fall.rotation.x + (Math.random() - 0.5) * 6,
    z: fall.rotation.z + (Math.random() - 0.5) * 6,
    duration: dur, ease: 'none'
  });
  gsap.to(fall.material, {
    opacity: 0, duration: dur * 0.4, delay: dur * 0.6, ease: 'power1.in',
    onComplete: () => { root.remove(fall); fall.material.dispose(); }
  });
}
